import { getBuildConfig } from '../../utils/core';
import type { InstitutionLoginProps } from '../institution-logins/institutionLoginsTypes';
import { type Institution, mkInstitution } from './institutionsTypes';

// Not all connected institution-logins have an institution associated with them. However, some components
// require an institution to be present. This is a list of static institutions that can be used in these cases.
//   Components that require an institution: Add-FI, Accounts Settings
//
// These institutions IDs should never be tied to an institution-login in QCS. They are only used for components
// that require an institution to be present. You can check the institution ID to see if it is a static institution
// and handle it accordingly.
//
// TODO: Add Kelly Blue Book (if we use) & Zillow to this list
//
const staticInstitutions = new Map<string, Institution>([
  [
    'STRIPE',
    mkInstitution({
      id: 'STATIC_STRIPE',
      name: 'Stripe',
      url: 'https://stripe.com',
      logoUrl: 'https://s3.us-west-2.amazonaws.com/images.services-stg.quickencs.com/logos/fis/stripe.svg',
      aggregator: 'STRIPE',
    }),
  ],
]);

export const getStaticInstitutionFromAggregator = (id: string): Institution | undefined => {
  return staticInstitutions.get(id);
};

export const getDefaultChannel = (institution: Institution, featureFlags?: any) => {
  switch (institution.aggregator) {
    case 'FDP':
      return institution?.fdp?.channelData?.type;
    case 'FDS':
      return institution?.fds?.channelData?.type;
    case 'FINICITY':
      // TODO: we should read this from institution
      return 'FINICITY';
    case 'PLAID': {
      // In Plaid, we can use different Plaid environments in each of the QCS environments. This allows us to use
      // the Plaid Sandbox environment in production.
      const plaidEnv = featureFlags?.get('plaidEnv') || getBuildConfig()?.plaid_env;
      if (plaidEnv === 'sandbox') {
        return 'PLAID_SANDBOX';
      }
      if (plaidEnv === 'development') {
        return 'PLAID_DEV';
      }
      return 'PLAID';
    }
    case 'STRIPE': {
      const stripeMode = featureFlags?.get('stripeMode') || getBuildConfig()?.stripe_mode;
      if (stripeMode === 'test') {
        return 'STRIPE_TEST';
      }
      return 'STRIPE';
    }
    case 'QUICKEN':
      return institution?.quicken?.channelData?.type;
    default:
      // should log warning
      break;
  }
  return undefined;
};

export const getAggregatorFromChannel = (channel: string) => {
  if (!channel) return '';
  const channelEnvStart = channel.indexOf('_');
  return channelEnvStart === -1 ? channel : channel.substr(0, channelEnvStart);
};

export type InstitutionTrackingProperties = {
  institution_id?: string;
  institution_name?: string;
};

// Funnel events identify the QCS institution for every aggregator; aggregator IDs are tracked separately.
export const getInstitutionTrackingProperties = ({
  institution,
  institutionLogin,
}: {
  institution?: Pick<Institution, 'id' | 'name'> | null;
  // Fallback when no institution is selected.
  institutionLogin?: Pick<InstitutionLoginProps, 'institutionId' | 'name'> | null;
}): InstitutionTrackingProperties => {
  if (institution) {
    return { institution_id: institution.id, institution_name: institution.name };
  }
  // QCS reports '0' when it only knows the aggregator's cpInstitutionId, which is not a QCS ID.
  if (institutionLogin?.institutionId && institutionLogin.institutionId !== '0') {
    return { institution_id: institutionLogin.institutionId, institution_name: institutionLogin.name };
  }
  return {};
};
